import PropTypes from "prop-types";
import { useMemo } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import DetailHeaderBar from "components/DetailHeaderBar";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import useAuthorized from "hooks/useAuthorized";
import { useContact } from "hooks/useContact";
import usePageTitle from "hooks/usePageTitle";
import ContactForm from "routes/contact/form/ContactForm";
import {
  BREADCRUMB_LABELS,
  MODEL_TYPE,
  PAGE_TITLES,
} from "routes/contact/form/constants";
import { FORM_MODES } from "routes/contact/form/defaults";
import { usePresetRecords } from "routes/contact/form/usePresetRecords";
import { contactPaths, readCreatePreset } from "routes/contact/paths";

const CenteredMessage = ({ children }) => (
  <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-3 tw:p-8 tw:text-center tw:text-(--color-text-muted)">
    {children}
  </div>
);

CenteredMessage.propTypes = { children: PropTypes.node };

const BackToList = () => <Link to={contactPaths.list}>Back to Contacts</Link>;

/**
 * The page chrome: the same header bar as the record page above a scrolling
 * body. The breadcrumb links back to the list and, on edit, to the contact.
 */
const PageShell = ({ mode, contactId, contactName, subtitle, children }) => (
  <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:font-sans tw:text-(--color-text-primary)">
    <DetailHeaderBar
      breadcrumb={
        <DetailBreadcrumb
          parentTo={contactPaths.list}
          parentLabel="All Contacts"
          trail={
            contactId
              ? [
                  {
                    to: contactPaths.detail(contactId),
                    label: contactName ?? `Contact (${contactId})`,
                  },
                ]
              : []
          }
          current={BREADCRUMB_LABELS[mode]}
        />
      }
      title={PAGE_TITLES[mode]}
      subtitle={subtitle}
    />
    {/* The gutter stays reserved so the form does not shift sideways as the
        scrollbar comes and goes. */}
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        {children}
      </div>
    </div>
  </div>
);

PageShell.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  contactId: PropTypes.string,
  contactName: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
};

/**
 * Names where a new contact will go, or null when the preset names nothing.
 */
const presetSummary = (preset, records) => {
  const parts = [
    records.entity && `Entity: ${records.entity.name}`,
    records.campaign && `Campaign: ${records.campaign.name}`,
    preset.leadId != null && `From lead #${preset.leadId}`,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

// `from` carries the query string, so the list may arrive as "/contacts?page=2".
const isListPath = path => path.split("?")[0] === contactPaths.list;

/** The full-page Contact create and edit form. */
const ContactFormPage = () => {
  // Only the edit route has an :id segment.
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = id ? FORM_MODES.edit : FORM_MODES.create;
  const isEdit = mode === FORM_MODES.edit;
  const from = location.state?.from;
  const returnTo = from ?? contactPaths.list;
  // Opened from a record page, such as the entity or campaign whose Contacts
  // tab started it, a save returns there. From the Contacts list, or opened
  // directly, it opens the saved contact.
  const isFromRecordPage = Boolean(from) && !isListPath(from);

  // The same permissions gate the add and edit actions on the Contact tables.
  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.entityContact.edit
      : ACL_RELATIONSHIPS.entityContact.create,
    ACL_RELATIONSHIPS.entityForFilters.read,
  ]);

  const preset = useMemo(
    () => (isEdit ? {} : readCreatePreset(searchParams)),
    [isEdit, searchParams]
  );

  const {
    data: record,
    isSuccess: isRecordLoaded,
    isFetchedAfterMount,
    isError: isRecordError,
    error: recordError,
  } = useContact(id);
  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously, and the form latches its defaults at the first render, so
  // mounting on it would save stale values over a newer edit.
  const isRecordReady = !id || (isRecordLoaded && isFetchedAfterMount);

  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);
  const presetQuery = usePresetRecords(preset);

  usePageTitle(PAGE_TITLES[mode]);

  const handleSaved = saved =>
    navigate(isFromRecordPage ? from : contactPaths.detail(saved.id));
  const handleCancel = () => navigate(returnTo);

  if (!canWrite) {
    return <Navigate to={contactPaths.list} replace />;
  }

  const shell = content => (
    <PageShell
      mode={mode}
      contactId={id}
      contactName={record?.full_name}
      subtitle={presetSummary(preset, presetQuery.records)}
    >
      {content}
    </PageShell>
  );

  if (isRecordError) {
    const isNotFound = recordError?.response?.status === 404;
    return shell(
      <CenteredMessage>
        <p>
          {isNotFound
            ? "This Contact does not exist."
            : "Could not load this Contact. Please try again."}
        </p>
        <BackToList />
      </CenteredMessage>
    );
  }

  if (configsQuery.isError || presetQuery.isError) {
    return shell(
      <CenteredMessage>
        <p>Could not load the form. Please try again.</p>
        <BackToList />
      </CenteredMessage>
    );
  }

  const isLoading =
    !isRecordReady ||
    configsQuery.isPending ||
    groupsQuery.isPending ||
    presetQuery.isPending;

  if (isLoading) {
    return shell(
      <CenteredMessage>
        <Spinner />
      </CenteredMessage>
    );
  }

  return shell(
    <ContactForm
      mode={mode}
      record={record}
      preset={preset}
      presetEntityName={presetQuery.records.entity?.name}
      customFieldConfigs={configsQuery.data ?? []}
      customFieldGroups={groupsQuery.data ?? []}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );
};

export default ContactFormPage;
